import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, GitDiff, Warning } from "@phosphor-icons/react";

export default function ObservatoryCompare() {
  const { id } = useParams();
  const [comp, setComp] = useState(null);

  useEffect(() => {
    api.get(`/observatory/comparisons/${id}`)
      .then((r) => setComp(r.data))
      .catch(() => toast.error("Comparaison introuvable"));
  }, [id]);

  if (!comp) return <div className="p-12 label-mono">Chargement...</div>;

  const result = comp.result || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/observatory" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="cmp-back">
        <ArrowLeft size={14} /> Retour à la veille
      </Link>

      <div className="border-b border-black pb-6 mb-8 fade-up">
        <div className="label-mono">§ Vita-Observatory · Comparaison croisée</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
          <GitDiff size={40} className="text-[var(--signal)]" /> Synthèse comparative
        </h1>
        <div className="mt-3 font-mono text-xs text-[var(--muted)]">
          {comp.report_ids.length} rapports · focus « {comp.focus} » · {fmtDate(comp.cree_le)}
        </div>
      </div>

      {result.synthese && (
        <div className="border border-black bg-[var(--paper)] p-8 mb-8 paper-grain fade-up">
          <div className="label-mono">§ Synthèse</div>
          <p className="mt-3 font-serif text-2xl leading-relaxed">{result.synthese}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15 mb-8">
        {result.divergences?.length > 0 && (
          <section className="bg-[var(--paper)] p-6">
            <div className="label-mono">§ Divergences clés</div>
            <h2 className="font-serif text-2xl mt-1">Désaccords entre sources</h2>
            <div className="mt-4 space-y-4">
              {result.divergences.map((d, i) => (
                <div key={i} className="border border-black/15 p-4" data-testid={`cmp-divergence-${i}`}>
                  <div className="font-serif text-lg">{d.indicateur}</div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(d.sources || []).map((s, j) => (
                      <div key={j} className="border border-black/20 p-2">
                        <div className="label-mono text-[9px]">{s.org}</div>
                        <div className="text-xl font-light tabular-nums mt-1">{s.valeur}</div>
                      </div>
                    ))}
                  </div>
                  {d.interpretation && <p className="mt-3 text-sm text-[var(--muted)] italic">{d.interpretation}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {result.convergences?.length > 0 && (
          <section className="bg-[var(--paper)] p-6">
            <div className="label-mono">§ Convergences</div>
            <h2 className="font-serif text-2xl mt-1">Points d'accord</h2>
            <ul className="mt-4 space-y-2 list-disc pl-5">
              {result.convergences.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        )}
      </div>

      {result.tableau_comparatif?.length > 0 && (
        <div className="border border-black/15 bg-[var(--paper)] p-6 mb-8">
          <div className="label-mono">§ Tableau comparatif</div>
          <h2 className="font-serif text-2xl mt-1">Indicateurs croisés</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-black/15 label-mono text-left">
                <tr>
                  {Object.keys(result.tableau_comparatif[0]).map((k) => <th key={k} className="p-3 capitalize">{k}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {result.tableau_comparatif.map((row, i) => (
                  <tr key={i} className="hover:bg-[var(--paper-2)]">
                    {Object.entries(row).map(([k, v]) => (
                      <td key={k} className={`p-3 ${k === "indicateur" ? "font-serif" : "tabular-nums"}`}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result.alertes_critiques?.length > 0 && (
        <div className="border border-[var(--signal)] bg-[var(--signal-light)] p-6 mb-8">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-[var(--signal)]" />
            <div className="label-mono text-[var(--signal-hover)]">§ Alertes critiques</div>
          </div>
          <ul className="mt-4 space-y-2">
            {result.alertes_critiques.map((a, i) => (
              <li key={i} className="font-serif text-lg leading-relaxed">— {a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
